import { Link, useNavigate } from "react-router-dom";
import { Star, Film, Tv, Sparkles, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

export const MovieCard = ({ movie, progress, className = "" }) => {
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const isFullWidth = className.includes("w-full");

  const handleClick = (e) => {
    if (!user) {
      e.preventDefault();
      toast.info("Regístrate gratis o inicia sesión para ver este contenido");
      navigate("/auth", { state: { from: `/titulo/${movie.slug}` } });
    }
  };

  const badgeText = movie.custom_badge || (movie.upcoming ? t("upcoming") : null);

  return (
    <Link
      to={`/titulo/${movie.slug}`}
      onClick={handleClick}
      data-testid={`movie-card-${movie.id}`}
      aria-label={`${movie.title} (${movie.year || "LGBT+"})`}
      className={`group relative block shrink-0 flex-shrink-0 transition-all duration-300 ease-out hover:-translate-y-1.5 hover:z-20 focus-visible:ring-2 focus-visible:ring-[#E11D48] rounded-xl outline-none ${
        isFullWidth ? "w-full" : "w-[140px] sm:w-[165px] md:w-[185px]"
      } ${className}`}
    >
      {/* Strictly locked 2:3 Aspect Ratio Container via inline style and relative box */}
      <div
        className={`relative w-full overflow-hidden bg-zinc-900 rounded-xl border ${
          movie.upcoming ? "border-amber-500/40 group-hover:border-amber-400/80 shadow-amber-500/10" : "border-white/10 group-hover:border-[#E11D48]/50"
        } shadow-md group-hover:shadow-xl group-hover:shadow-[#E11D48]/15 transition-all shrink-0`}
        style={{ aspectRatio: "2 / 3" }}
      >
        {movie.poster_url ? (
          <img
            src={movie.poster_url}
            alt={movie.title}
            className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "https://images.unsplash.com/photo-1485846234645-a62644f84728?w=500&auto=format&fit=crop&q=80";
            }}
          />
        ) : (
          <div className="absolute inset-0 w-full h-full flex items-center justify-center text-zinc-500 text-xs p-3 text-center bg-zinc-900 font-medium">
            {movie.title}
          </div>
        )}

        {/* Gradient Overlay on Hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-between p-2.5 pointer-events-none" />

        {/* Top Badges Area */}
        <div className="absolute top-2 left-2 right-2 z-10 flex items-center justify-between gap-1 pointer-events-none">
          {/* Media Type Badge */}
          <span className="text-[10px] font-bold uppercase tracking-wider bg-black/80 backdrop-blur-md text-white px-2 py-0.5 rounded-full border border-white/10 flex items-center gap-1 shadow-sm">
            {movie.media_type === "tv" ? <Tv size={10} className="text-blue-400" /> : <Film size={10} className="text-amber-400" />}
            {movie.media_type === "tv" ? "Serie" : "Peli"}
          </span>

          {/* Próximo Estreno / Custom Badge */}
          {badgeText && (
            <span
              data-testid={`badge-upcoming-${movie.id}`}
              className="text-[9px] font-extrabold uppercase tracking-wider bg-gradient-to-r from-amber-500 to-rose-500 text-white px-2 py-0.5 rounded-full shadow-lg flex items-center gap-1 border border-amber-300/30 animate-pulse"
            >
              <Sparkles size={9} className="text-amber-200" />
              <span className="truncate max-w-[85px]">{badgeText}</span>
            </span>
          )}
        </div>

        {/* Release Date Ribbon if Upcoming */}
        {movie.upcoming && movie.release_date && (
          <div className="absolute bottom-2 left-2 right-2 z-10">
            <span className="text-[9px] font-semibold bg-black/85 backdrop-blur-md text-amber-300 border border-amber-500/30 px-2 py-0.5 rounded flex items-center justify-center gap-1 shadow">
              <Clock size={10} /> {movie.release_date}
            </span>
          </div>
        )}

        {/* Progress Bar for Continue Watching */}
        {progress && progress.duration > 0 && (
          <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-white/20 z-10">
            <div
              className="h-full bg-[#E11D48] rounded-r-full shadow-sm"
              style={{ width: `${Math.min(100, (progress.position / progress.duration) * 100)}%` }}
            />
          </div>
        )}
      </div>

      <div className="mt-2.5 px-0.5">
        <p className="text-xs sm:text-sm font-semibold truncate text-zinc-100 group-hover:text-[#E11D48] transition-colors">
          {movie.title}
        </p>
        <div className="flex items-center justify-between text-[11px] text-zinc-400 mt-1 font-medium">
          <span>{movie.year || (movie.upcoming ? "Próximamente" : "2024")}</span>
          {movie.avg_rating > 0 && !movie.upcoming && (
            <span className="flex items-center gap-0.5 text-amber-400 font-semibold">
              <Star size={11} className="fill-amber-400 text-amber-400" /> {movie.avg_rating}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};
